import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import { Dimensions, Keyboard, View as RNView } from "react-native";
import { BottomSheetModal } from "@gorhom/bottom-sheet";
import { useUnistyles } from "react-native-unistyles";
import { View } from "@src/components";
import { Icon } from "@src/components/Icon";
import { Text } from "@src/components/Text";
import { AppBottomSheet } from "@src/features/common/components/AppBottomSheet";
import { CameraSearchWithCrossTenant } from "./CameraSearchWithCrossTenant.native";

const SCREEN_HEIGHT = Dimensions.get("window").height;
const TOP_INSET = Math.round(SCREEN_HEIGHT * 0.3);

interface CameraSearchSheetProps {
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
}

export interface CameraSearchSheetHandle {
  present: () => void;
}

export const CameraSearchSheet = forwardRef<CameraSearchSheetHandle, CameraSearchSheetProps>(
  ({ searchQuery, onSearchQueryChange }, ref) => {
    const { theme } = useUnistyles();
    const sheetRef = useRef<BottomSheetModal>(null);
    const snapPoints = useMemo(() => ["80%", "100%"], []);
    const showInstructions = !searchQuery.trim();

    useImperativeHandle(ref, () => ({
      present: () => sheetRef.current?.present(),
    }));

    const handleResultSelected = useCallback(() => {
      sheetRef.current?.dismiss();
    }, []);

    const handleAnimate = useCallback((_fromIndex: number, toIndex: number) => {
      if (toIndex === -1) {
        Keyboard.dismiss();
      }
    }, []);

    return (
      <AppBottomSheet
        bottomSheetRef={sheetRef}
        snapPoints={snapPoints}
        enableDynamicSizing={false}
        topInset={TOP_INSET}
        onAnimate={handleAnimate}
        backgroundStyle={{ backgroundColor: theme.colors.background }}
      >
        <RNView style={{ flex: 1, paddingHorizontal: theme.spacing["5xl"], paddingTop: theme.spacing.lg }}>
          <CameraSearchWithCrossTenant
            searchQuery={searchQuery}
            onSearchQueryChange={onSearchQueryChange}
            size="small"
            onResultSelected={handleResultSelected}
          />
          {showInstructions && (
            <View alignItems="center" gap="lg" pt="7xl" pb="5xl">
              <Icon name="search" size={40} color={theme.colors.textMuted} style={{ opacity: 0.5 }} />
              <Text textStyle="sm/medium" color="textMuted" textAlign="center">
                Search across all your organisations
              </Text>
              <Text textStyle="xs/regular" color="textMuted" textAlign="center" px="5xl">
                Enter a camera ID to find it across all organisations you have access to. The CAM prefix is optional.
              </Text>
            </View>
          )}
        </RNView>
      </AppBottomSheet>
    );
  },
);

CameraSearchSheet.displayName = "CameraSearchSheet";
